import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/Label'
import { Card } from '@/components/ui/Card'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/Badge'
import useGetCurrentUser from '@/services/api/auth/useGetCurrentUser'
import useDisableTwoFactor from '@/services/api/auth/tfa/useDisableTwoFactor'
import TwoFactorSetupDialog from './TwoFactorSetupDialog'
import {
  Shield,
  ShieldCheck,
  ShieldX,
  Loader2,
  AlertTriangle,
} from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import Field from '@/components/ui/Field'
import FieldStatusMessage from '@/components/ui/FieldStatusMessage'
import { PasswordInput } from '@/components/PasswordInput'

export default function TwoFactorSection() {
  const { data: userData, status } = useGetCurrentUser()
  const [showSetupDialog, setShowSetupDialog] = useState(false)
  const [showDisableDialog, setShowDisableDialog] = useState(false)

  const disableSchema = z.object({
    password: z.string().min(1, 'Password is required'),
  })

  const disableForm = useForm({
    resolver: zodResolver(disableSchema),
    defaultValues: { password: '' },
    mode: 'onSubmit',
  })

  const disableTwoFactor = useDisableTwoFactor()

  const handleEnable2FA = () => {
    setShowSetupDialog(true)
  }

  const handleDisableSubmit = async (values: { password: string }) => {
    disableForm.clearErrors()
    const success = await disableTwoFactor.action({ password: values.password })

    userData.is_tfa_enabled = false
    setShowDisableDialog(false)
    disableForm.reset()

    if (success) {
      setShowDisableDialog(false)
      disableForm.reset()
    }
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    disableForm.setValue('password', value)

    if (disableForm.formState.errors.password) {
      disableForm.clearErrors('password')
    }
  }

  if (status === 'pending') {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center">
          <Loader2 className="size-6 animate-spin" />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="mb-2">Two-Factor Authentication</h2>
        <p className="mb-4 text-txt-secondary">
          Add an extra layer of security to your account using an authenticator
          app.
        </p>
      </div>

      <Card className="w-full p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {userData.is_tfa_enabled ? (
              <ShieldCheck className="h-8 w-8 text-green-600" />
            ) : (
              <ShieldX className="h-8 w-8 text-gray-400" />
            )}
            <div>
              <div className="mb-1 flex items-center gap-2">
                <span>Two-Factor Authentication</span>
                <Badge
                  variant={userData.is_tfa_enabled ? 'default' : 'secondary'}
                >
                  {userData.is_tfa_enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
              <p className="text-sm text-txt-secondary">
                {userData.is_tfa_enabled
                  ? 'Your account is protected with 2FA. Keep your recovery codes safe.'
                  : 'Secure your account with an authenticator app like Google Authenticator or Authy'}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {userData.is_tfa_enabled ? (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowDisableDialog(true)}
              >
                Disable
              </Button>
            ) : (
              <Button onClick={handleEnable2FA}>Enable 2FA</Button>
            )}
          </div>
        </div>
      </Card>

      {userData.is_tfa_enabled && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
            <div className="text-sm text-blue-800">
              <p className="mb-2">
                <strong>Security Notice:</strong>
              </p>
              <ul className="space-y-1 text-xs">
                <li>• Keep your recovery codes safe and accessible</li>
                <li>
                  • Both viewing and generating recovery codes require password
                  confirmation
                </li>
                <li>• Store codes securely (password manager recommended)</li>
                <li>• Each new code generation invalidates previous codes</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {showSetupDialog && (
        <TwoFactorSetupDialog
          open={showSetupDialog}
          onOpenChange={setShowSetupDialog}
          onSuccess={() => {}}
        />
      )}

      {/* Disable 2FA Dialog */}
      <Dialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Disable Two-Factor Authentication
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <form
              onSubmit={disableForm.handleSubmit(handleDisableSubmit)}
              className="space-y-4"
            >
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
                  <div className="text-sm text-red-800">
                    <p>
                      <strong>Warning:</strong> Disabling 2FA will make your
                      account less secure.
                    </p>
                    <p className="mt-1">
                      You'll only need your password to sign in, which increases
                      the risk if your password is compromised.
                    </p>
                  </div>
                </div>
              </div>

              <Field
                statusText={disableForm.formState.errors?.password?.message?.toString()}
                status={disableForm.formState.errors?.password ? 'error' : null}
              >
                <Label htmlFor="disable-password">Current Password</Label>

                <PasswordInput
                  id="disable-password"
                  {...disableForm.register('password')}
                  onChange={handlePasswordChange}
                  placeholder="Enter your password"
                />
                <FieldStatusMessage />
              </Field>

              <div className="flex gap-3">
                <Button
                  type="submit"
                  variant="destructive"
                  disabled={disableTwoFactor.status.status === 'pending'}
                  className="flex-1"
                >
                  {disableTwoFactor.status.status === 'pending'
                    ? 'Disabling...'
                    : 'Disable 2FA'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowDisableDialog(false)}
                  disabled={disableTwoFactor.status.status === 'pending'}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogBody>
        </DialogContent>
      </Dialog>
    </div>
  )
}
